import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pt } from "date-fns/locale";
import { FC } from "react";
import { BrowserRouter } from "react-router-dom";
import { DialogProvider } from "./components/DialogManager";
import { ToastProvider } from "./components/UIComponents/Toast";
import {
  AuthProvider,
  EmployeesProvider,
  RefOptionsProvider,
  StoreProvider,
} from "./contexts";
import { useIframeAutoResize } from "./hooks/useIframeAutoResize";
import AppRouter from "./routes/AppRouter";

const AppInner: FC = () => {
  // Auto-resize when embedded in an iframe: parent removes scrollbars but keeps full content visible.
  useIframeAutoResize({ heightPadding: 1 });

  // Create a custom locale with capitalized month names (both full and short)
  const customPtLocale = {
    ...pt,
    localize: {
      ...pt.localize,
      month: (
        n: Parameters<typeof pt.localize.month>[0],
        options?: Parameters<typeof pt.localize.month>[1],
      ) => {
        const month = pt.localize.month(n, options);
        // Capitalize the first letter for both full and short month names
        return month.charAt(0).toUpperCase() + month.slice(1);
      },
    },
  };

  return (
    <AuthProvider>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={customPtLocale}
      >
        <StoreProvider>
          <EmployeesProvider>
            <RefOptionsProvider>
              <ToastProvider>
                <DialogProvider>
                  <Box>
                    <AppRouter />
                  </Box>
                </DialogProvider>
              </ToastProvider>
            </RefOptionsProvider>
          </EmployeesProvider>
        </StoreProvider>
      </LocalizationProvider>
    </AuthProvider>
  );
};

const App: FC = () => (
  <BrowserRouter>
    <AppInner />
  </BrowserRouter>
);

export default App;
